import React from 'react'
import { Language } from "../languages/labels/language.labels"
import { AccountingAccount } from "../models/accounting.account.model"
import { AccountingEntry } from "../models/accounting.entry.model"
import { TreasuryAccount } from "../models/treasury.account.model"
import { Address } from "../models/address.model"
import { Contact } from "../models/contact.model"
import { Currency } from "../models/currency.model"
import { Customer } from "../models/customer.model"
import { InventoryReference } from "../models/inventory.reference.model"
import { Deadline } from "../models/deadline.model"
import { Provider } from "../models/provider.model"
import { Credit } from "../models/credit.model"
import { Debit } from "../models/debit.model"
import { dateFormatLocalized } from "./date.time.format.script"
import { deadlineTypeValue } from "../libraries/deadline.type.library"
import { countryName } from "../libraries/country.library"
import { treasuryAccountSelected } from "./list.selected.script"

export function formatPercentage(amount?: number | null): string {
  const val = amount ?? 0
  return (val === 100) ? `${val}%` : `${val.toFixed(2)}%`
}

export function formatMoney(amount?: number | null): string {
  return (amount) ? amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'
}

export function amountClass(v: number | null | undefined): string {
  return (v !== null && v !== undefined && v < 0) ? 'text-danger' : ''
}

export const tabularStyle: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' }

export function formatTaxRate(tax_rate?: number | null): string {
  return `${((tax_rate ?? 0) * 100).toFixed(2)}`
}

export function formatCurrency(currency?: Currency | null): string {
  const code = currency?.code ?? 'CHF'
  const name = currency?.name ?? ''
  const symbol = currency?.symbol ?? ''
  return `${code} : ${name} (${symbol})`
}

export function formatContentDetail(content?: InventoryReference | null): string {
  if (!content) return ''
  return [content.code && `${content.code},`, content.display_name, content.type === 'product' && content.description].filter(Boolean).join(' ')
}

export function formatContact(contact: any): string {
  if (!contact) return ''

  const data = contact.data ?? contact
  const type = contact.type
  let baseName = ''

  if (type === 'person' || (data.first_name && data.last_name && !data.name)) {
    const personName = `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim()
    baseName = personName || data.name || ''
  } else if (data.name) {
    baseName = data.name
  } else {
    baseName = `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim() || ''
  }

  return baseName
}

export function normalizeContactData(contact: any): any {
  if (!contact) return contact
  if (!contact.data) {
    contact.data = contact
  }
  contact.type = contact.type ?? ''
  const d = contact.data

  // strings
  d.contact_code    = d.contact_code    ?? ''
  d.uuid            = d.uuid            ?? ''
  d.name            = d.name            ?? ''
  d.first_name      = d.first_name      ?? ''
  d.last_name       = d.last_name       ?? ''
  d.logo            = d.logo            ?? ''
  d.avatar          = d.avatar          ?? ''
  d.email           = d.email           ?? ''
  d.phone           = d.phone           ?? ''
  d.website         = d.website         ?? ''
  d.register_number = d.register_number ?? ''
  d.vat_number      = d.vat_number      ?? ''
  d.language        = d.language        ?? ''
  d.qr_type         = d.qr_type         ?? ''

  // address
  const a = d.address ?? {}
  d.address = {
    street:              a.street              ?? '',
    number:              a.number              ?? '',
    additional:          a.additional          ?? '',
    administrative_area: a.administrative_area ?? '',
    city:                a.city                ?? '',
    zip_code:            a.zip_code            ?? '',
    country:             a.country             ?? '',
  }

  // bank account
  const b = d.bank_account ?? {}
  d.bank_account = {
    name: b.name ?? '',
    iban: b.iban ?? '',
    bban: b.bban ?? '',
    bic:  b.bic  ?? '',
  }

  // currency
  const c = d.currency ?? {}
  d.currency = {
    code:   c.code   ?? '',
    name:   c.name   ?? '',
    symbol: c.symbol ?? '',
  }

  // arrays
  d.companies = d.companies ?? []
  d.addresses = d.addresses ?? []

  return contact
}

export function normalizeContacts(contacts: any[]): any[] {
  (contacts ?? []).forEach(normalizeContactData)
  return contacts ?? []
}

export function formatBIC(lang: Language, bic?: string | null): string {
  const val = bic ?? ''
  return (val.length > 0) ? `${val.substring(0, 4)} ${val.substring(4, 6)} ${val.substring(6, 8)} ${val.substring(8, val.length)}` : lang.labels.none
}

export function formatIBAN(lang: Language, iban?: string | null): string {
  const val = iban ?? ''
  return (val.length > 0) ? (val.match(/.{1,4}/g) ?? []).join(' ') : lang.labels.none
}

export function formatNumberAddress(lang: Language, number?: string | null): string {
  if (!number) return ''
  return (number === 'NN' || number === 'nn' || number === '') ? lang.labels.noNumber : number.toUpperCase()
}

export function formatAddress(lang: Language, address?: Address | null): string {
  if (!address) return ''
  return `${address.number ?? ''} ${address.street ?? ''}, ${address.zip_code ?? ''}, ${address.city ?? ''}, ${countryName(lang.code, address.country ?? '')}`
}

export function formatLocation(lang: Language, address?: Address | null): string {
  if (!address) return ''
  return `${(address.city ?? '').length > 0 ? `${address.city}, ` : ''}${(address.country ?? '').length ? countryName(lang.code, address.country ?? '') : ''}`
}

export function formatDeadline(lang: Language, deadline?: Deadline | null): string {
  if (!deadline) return ''
  return `${deadline.quantity ?? 0} ${deadlineTypeValue(lang, deadline.type ?? '', (deadline.quantity ?? 0) > 1).toLowerCase()}`
}

export function formatAccountingAccount(account?: AccountingAccount | null, company_language?: string | null): string {
  if (!account) return ''
  let name: string = ''
  if (account.name) {
    if (typeof account.name === 'string') {
      name = account.name
    } else {
      name = (account.name as any)[(company_language ?? 'fr').toLocaleLowerCase()] ?? (account.name as any).fr ?? (account.name as any).en ?? Object.values(account.name)?.[0] ?? ''
    }
  }
  return `${account.code ?? ''}, ${name}`
}

export function formatAccountingEntry(lang: Language, currency?: Currency | null, entry?: AccountingEntry | null, treasury_accounts?: TreasuryAccount[] | null, type?: string | null): string {
  if (!entry) return ''
  if (type === 'debit' || type === 'credit') {
    let movement: Credit | Debit | undefined = (entry[type === 'debit' ? 'credits' : 'debits'] ?? []).find((item) => (item.type === 'treasury-account'))
    let treasury: string = movement?.reference?.id_treasury_account ? treasuryAccountSelected(treasury_accounts ?? [], movement.reference.id_treasury_account) : ''
    return [dateFormatLocalized(lang.code, entry.date), treasury, `${currency?.code ?? ''} ${formatMoney(entry.balance)}`].filter(Boolean).join(', ')
  } else {
    return lang.labels.undefined
  }
}
